import { useEffect, useState, useRef } from 'react';

import {
  Collapse,
  Tooltip,
  Typography,
  Modal,
  Row,
  Col,
  Spin,
  Form,
  Input,
  DatePicker,
  Button,
  InputNumber,
  Empty,
  Select,
  Alert,
  Radio,
  Divider,
  Space,
  AutoComplete,
} from 'antd';

import classnames from 'classnames';

import {
  EditOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';

import { WrapperSelect } from 'common/components';

import {
  productDetailActionTypes,
  useProductDetail,
  useProductDetailDispatch,
} from '../components/product-new-detail-view/ProductDetailContext';

import {
  checkDisabledFormField,
  getDataByFieldFullPath,
  checkRequiredFormField,
  formatCurrencyValue,
  parseCurrencyValue,
} from './utils';
import { isPricingField } from '../constants';

import sortByKey from 'utils/sortByKey';

import {
  checkIsUSRegion,
  getFormValuesAfterUpdatingValueField,
} from '../components/product-new-detail-view/allergenUtils';

import { DATE_FORMAT } from 'static/Constants';
import {
  DEFAULT_US_ALLERGENS,
  DEFAULT_WW_ALLERGENS,
} from 'static/AllergenConstants';
import { getFieldValueFromListIdx } from '../components/product-new-detail-view/utils';
import {
  filterInvalidValues,
  generateOriginalStringPath,
} from '../components/product-new-detail-view/overlay/utils';

import BrickCodeInput from 'common/components/product-add/BrickCodeInput';
import {
  getClassesByCategoryAndSubcategory,
  getSubcategoriesByCategory,
} from 'pages/product-full-view/components/product-new-detail-view/hierarchyCategoryUtils';
import { CATEGORY_FIREARMS } from 'pages/product-full-view/components/product-new-detail-view/constants';

export const ButtonEditModuleProperties = ({
  moduleName,
  moduleDisplayName,
  onClick,
}) => {
  return (
    <>
      <Tooltip placement='left' title={`Edit ${moduleDisplayName}`}>
        <EditOutlined
          className='product-detail-view__edit-icon'
          onClick={(event) => {
            event.stopPropagation();
            onClick(moduleName, moduleDisplayName);
          }}
        />
      </Tooltip>
    </>
  );
};

export const CollapseView = ({
  children,
  headerPanel,
  keyPanel,
  extraPanel,
  className,
  ...otherProps
}) => {
  return (
    <Collapse
      bordered={false}
      ghost
      expandIconPosition='right'
      className={classnames({
        'product-detail-view__collapse': true,
        [className]: Boolean(className),
      })}
      {...otherProps}
    >
      <Collapse.Panel
        header={headerPanel}
        key={keyPanel}
        extra={typeof extraPanel === 'function' && extraPanel()}
      >
        {children}
      </Collapse.Panel>
    </Collapse>
  );
};

export const ModalForm = ({ children, ...otherProps }) => {
  return (
    <Modal
      title='Edit Information'
      width={'70vw'}
      bodyStyle={{ maxHeight: '80vh' }}
      closable={true}
      maskClosable={false}
      destroyOnClose
      centered
      {...otherProps}
    >
      {children}
    </Modal>
  );
};

export const ModalEditForm = ({ children, ...otherProps }) => {
  return <ModalForm {...otherProps}>{children}</ModalForm>;
};

export const HeaderGroupElement = ({
  header,
  style,
  className,
  ...otherProps
}) => {
  return (
    <div
      style={{
        marginTop: -12,
        zIndex: 20,
        ...style,
      }}
    >
      {header && (
        <div
          className={classnames({
            'product-detail__group-header': true,
            [className]: !!className,
          })}
          {...otherProps}
        >
          {header}
        </div>
      )}
    </div>
  );
};

export const GroupElementWrapper = ({
  children,
  title,
  style,
  ...otherProps
}) => {
  return (
    <div
      className='product-detail-view__group-element-wrapper'
      style={{
        border: '1px solid #c2cfe0',
        boxShadow: '0px 4px 10px rgb(0 0 0 / 6%)',
        borderRadius: '4px',
        width: '100%',
        ...style,
      }}
      {...otherProps}
    >
      {children}
    </div>
  );
};

export const PropertyLabel = ({ children, className, ...otherProps }) => {
  return (
    <Typography.Text
      strong
      ellipsis
      className={classnames(
        'product-detail__text-info product-detail__text-info--label',
        className
      )}
      {...otherProps}
    >
      {children}
    </Typography.Text>
  );
};

export const PropertyInfo = ({ children, className, ...otherProps }) => {
  return (
    <Typography.Text
      className={classnames('product-detail__text-info', className)}
      {...otherProps}
    >
      {children}
    </Typography.Text>
  );
};

export const PropertyText = ({ property, data }) => {
  const { fieldFullPath, propertyDisplayName } = property;

  const value = getDataByFieldFullPath(data, fieldFullPath);

  return (
    <>
      {value && (
        <Row>
          <Col span={12}>
            <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
          </Col>
          <Col span={12}>
            <PropertyInfo>{value}</PropertyInfo>
          </Col>
        </Row>
      )}
    </>
  );
};

export const PropertyNumber = ({ property, data }) => {
  const { propertyDisplayName, fieldFullPath } = property;

  const value = getDataByFieldFullPath(data, fieldFullPath);
  const isValidNumber = value !== null && value !== undefined;

  return (
    <>
      {isValidNumber && (
        <Row>
          <Col span={12}>
            <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
          </Col>
          <Col span={12}>
            <PropertyInfo>
              {isPricingField(fieldFullPath)
                ? formatCurrencyValue(value)
                : value}
            </PropertyInfo>
          </Col>
        </Row>
      )}
    </>
  );
};

export const PropertyDatetime = ({ property, data }) => {
  const { fieldFullPath, propertyDisplayName } = property;

  const value = getDataByFieldFullPath(data, fieldFullPath);

  return (
    <>
      {value && (
        <Row>
          <Col span={12}>
            <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
          </Col>
          <Col span={12}>
            <PropertyInfo>
              {value.format(DATE_FORMAT.SYSTEM_FORMAT)}
            </PropertyInfo>
          </Col>
        </Row>
      )}
    </>
  );
};

export const PropertyBoolean = ({ property, data }) => {
  const { propertyDisplayName, fieldFullPath } = property;

  const value = getDataByFieldFullPath(data, fieldFullPath);

  const isBooleanType = typeof value === 'boolean';

  // The customer request specified by ticket ID 7832.
  // const hasSpecialField = checkSpecialField(fieldFullPath);

  return (
    <>
      {isBooleanType && (
        <Row>
          <Col span={12}>
            <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
          </Col>
          <Col span={12}>
            <Radio.Group value={value}>
              <Radio style={{ marginLeft: 8 }} value={true}>
                Yes
              </Radio>
              <Radio style={{ marginLeft: 8 }} value={false}>
                No
              </Radio>
            </Radio.Group>
          </Col>
        </Row>
      )}
    </>
  );
};

export const PropertyList = ({ property, data }) => {
  const { fieldFullPath, propertyDisplayName, propertyType } = property;

  const values = getDataByFieldFullPath(data, fieldFullPath) ?? [];

  let filteredValues = filterInvalidValues(values);

  filteredValues = filteredValues.map((item) =>
    propertyType === 'datetime' ? item.format(DATE_FORMAT.SYSTEM_FORMAT) : item
  );

  return (
    <>
      {filteredValues.length > 0 && (
        <Row>
          <Col span={12}>
            <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
          </Col>
          <Col span={12}>
            <PropertyInfo>{filteredValues.join(', ')}</PropertyInfo>
          </Col>
        </Row>
      )}
    </>
  );
};

export const PropertyEnum = ({ propertyDisplayName, value }) => {
  return (
    <>
      {value && (
        <Row>
          <Col span={12}>
            <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
          </Col>
          <Col span={12}>
            <PropertyInfo>{value}</PropertyInfo>
          </Col>
        </Row>
      )}
    </>
  );
};

export const PropertyRichText = ({ property, data }) => {
  const { fieldFullPath, propertyDisplayName } = property;

  const value = getDataByFieldFullPath(data, fieldFullPath);

  return (
    <>
      {value && (
        <Row>
          <Col span={12}>
            <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
          </Col>
          <Col span={12}>
            <PropertyInfo style={{ whiteSpace: 'pre-line', margin: 0 }}>
              {value}
            </PropertyInfo>
          </Col>
        </Row>
      )}
    </>
  );
};

export const Loading = ({ style, ...otherProps }) => {
  const loadingIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...style,
      }}
      {...otherProps}
    >
      <Spin indicator={loadingIcon} />
    </div>
  );
};

// Form

const getPropsFormItem = (property) => {
  if (!property) return null;

  return {
    name: property.propertyName,
    label: property.propertyDisplayName ? (
      <LabelFormItem
        label={property.propertyDisplayName}
        tooltip={property.propertyDescription}
        fieldFullPath={property.fieldFullPath}
      />
    ) : null,
  };
};

export const LabelFormItem = ({ label, fieldFullPath, tooltip }) => {
  const {
    advancedModule: { foundFieldFullPath: advancedFoundFieldFullPath },
    baseModule: { foundFieldFullPath: baseFoundFieldFullPath },
  } = useProductDetail();

  return (
    <>
      <Tooltip title={label}>
        <Typography.Text
          ellipsis
          className={classnames({
            'product-detail-view__label-form-item': true,
            [`product-detail-view__label-form-item--${fieldFullPath}`]: true,
            'product-detail-view__label-form-item--found-field':
              fieldFullPath?.toLowerCase() ===
                baseFoundFieldFullPath?.toLowerCase() ||
              fieldFullPath?.toLowerCase() ===
                advancedFoundFieldFullPath?.toLowerCase(),
          })}
          strong
        >
          {label}
        </Typography.Text>
      </Tooltip>

      {tooltip && (
        <Tooltip title={tooltip} color='#238dd9'>
          <QuestionCircleOutlined className='product-detail-view__hint-button' />
        </Tooltip>
      )}
    </>
  );
};

export const FormItemContainer = ({ property, children, ...otherProps }) => {
  return (
    <Form.Item {...getPropsFormItem(property)} {...otherProps}>
      {children}
    </Form.Item>
  );
};

export const FormItemText = ({
  property,
  placeholder,
  maxLength,
  disabled,
  ...otherProps
}) => {
  return (
    <FormItemContainer property={property} {...otherProps}>
      <Input
        placeholder={
          placeholder
            ? placeholder
            : `Please Input ${property.propertyDisplayName}`
        }
        showCount={maxLength ? true : false}
        maxLength={maxLength}
        disabled={disabled}
      />
    </FormItemContainer>
  );
};

export const FormItemBhtsCode = ({
  property,
  placeholder,
  maxLength = 10,
  disabled,
  ...otherProps
}) => {
  const form = Form.useFormInstance();

  const handleChange = (event) => {
    const numericValue = event.target.value.replace(/\D/g, '');
    form.setFieldsValue({ [property.propertyName]: numericValue });
  };

  return (
    <FormItemContainer property={property} {...otherProps}>
      <Input
        placeholder={
          placeholder
            ? placeholder
            : `Please Input ${property.propertyDisplayName}`
        }
        showCount
        maxLength={maxLength}
        disabled={disabled}
        onChange={handleChange}
      />
    </FormItemContainer>
  );
};

export const FormItemBrick = ({
  property,
  placeholder,
  maxLength,
  disabled,
  ...otherProps
}) => {
  const form = Form.useFormInstance();
  const brickCode = Form.useWatch('BrickCode', form);
  const brickCodeName = Form.useWatch('BrickCodeName', form);

  const updateBrickCodeData = (brickCodeData) => {
    form.setFieldsValue({
      BrickCode: brickCodeData?.brickCode,
      BrickCodeName: brickCodeData?.brickCodeName,
    });
  };

  return (
    <FormItemContainer property={property} {...otherProps}>
      <BrickCodeInput
        updateBrickCodeData={updateBrickCodeData}
        initialValue={{ brickCode, brickCodeName }}
      />
      {/*
      <Input
        placeholder={
          placeholder
            ? placeholder
            : `Please Input ${property.propertyDisplayName}`
        }
        showCount={maxLength ? true : false}
        maxLength={maxLength}
        disabled={disabled}
      /> */}
    </FormItemContainer>
  );
};

/* True, False, Null */
export const FormItemBoolean = ({
  property,
  onChange = () => {},
  disabled,
  ...otherProps
}) => {
  // const { fieldFullPath } = property;

  // The customer request specified by ticket ID 7832.
  // const hasSpecialField = checkSpecialField(fieldFullPath);

  return (
    <FormItemContainer property={property} {...otherProps}>
      <Radio.Group disabled={disabled} onChange={onChange}>
        <Radio value={true}>Yes</Radio>
        <Radio value={false}>No</Radio>
      </Radio.Group>
    </FormItemContainer>
  );
};

/* True, False, Null */
export const FormItemAllergenBoolean = ({
  property,
  parentNestedIndex,
  onChange,
  disabled,
  ...otherProps
}) => {
  const { formInstance } = useProductDetail();

  const indexAllergenTypeCode = parentNestedIndex.concat('AllergenTypeCode');
  const indexRegion = parentNestedIndex.concat('Regional');

  const allergenTypeCode = Form.useWatch(indexAllergenTypeCode, formInstance);
  const region = Form.useWatch(indexRegion, formInstance);

  const isUS = checkIsUSRegion(region);

  const isDefaultField = isUS
    ? DEFAULT_US_ALLERGENS.includes(allergenTypeCode?.toLowerCase())
    : DEFAULT_WW_ALLERGENS.includes(allergenTypeCode?.toLowerCase());

  const isDisabled = Boolean(isDefaultField); // The default field cannot be published, while the custom field is optional.

  return (
    <FormItemBoolean
      property={property}
      onChange={onChange}
      disabled={isDisabled}
      {...otherProps}
    />
  );
};

export const FormItemDatetime = ({ property, ...otherProps }) => {
  return (
    <FormItemContainer property={property} {...otherProps}>
      <DatePicker format='MM-DD-YYYY' />
    </FormItemContainer>
  );
};

export const FormItemNumber = ({
  property,
  placeholder,
  maxLength,
  min = 0,
  ...otherProps
}) => {
  const isPricing = isPricingField(property.fieldFullPath);

  return (
    <FormItemContainer property={property} {...otherProps}>
      {maxLength ? (
        <InputNumber
          placeholder={
            placeholder
              ? placeholder
              : `Please Input ${property.propertyDisplayName}`
          }
          step='0.1'
          stringMode
          min={min}
          formatter={isPricing ? formatCurrencyValue : undefined}
          parser={isPricing ? parseCurrencyValue : undefined}
        />
      ) : (
        <InputNumber
          placeholder={
            placeholder
              ? placeholder
              : `Please Input ${property.propertyDisplayName}`
          }
          min={min}
          formatter={isPricing ? formatCurrencyValue : undefined}
          parser={isPricing ? parseCurrencyValue : undefined}
        />
      )}
    </FormItemContainer>
  );
};

export const FormItemDecimalNumber = ({
  property,
  placeholder,
  maxLength,
  step = 0.01,
  precision = 2,
  suffix,
  formatter,
  parser,
  min = 0,
  ...otherProps
}) => {
  const isPricing = isPricingField(property.fieldFullPath);

  // Block non-numeric characters on keypress
  const handleKeyPress = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    const value = e.target.value;

    // Allow: numbers (0-9), decimal point, backspace, delete, arrow keys, tab
    if (
      (charCode >= 48 && charCode <= 57) || // numbers 0-9
      charCode === 46 || // decimal point
      charCode === 8 || // backspace
      charCode === 9 || // tab
      charCode === 37 || // left arrow
      charCode === 39 // right arrow
    ) {
      // Prevent multiple decimal points
      if (charCode === 46 && value.includes('.')) {
        e.preventDefault();
        return;
      }

      // Prevent more than 2 decimal places
      if (value.includes('.')) {
        const decimalPart = value.split('.')[1];
        const cursorPosition = e.target.selectionStart;
        const decimalPosition = value.indexOf('.');

        if (
          decimalPart.length >= precision &&
          cursorPosition > decimalPosition
        ) {
          e.preventDefault();
          return;
        }
      }

      return;
    }

    e.preventDefault();
  };

  // Prevent pasting negative values
  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData('text');

    // Check if the pasted value contains a minus sign or is negative
    if (pastedText.includes('-') || parseFloat(pastedText) < 0) {
      e.preventDefault();
      return;
    }

    // Validate the pasted value is a valid positive number
    const numValue = parseFloat(pastedText);
    if (isNaN(numValue)) {
      e.preventDefault();
      return;
    }

    // Allow default paste behavior for valid positive numbers
  };

  return (
    <FormItemContainer property={property} {...otherProps}>
      <InputNumber
        placeholder={
          placeholder
            ? placeholder
            : `Please Input ${property.propertyDisplayName}`
        }
        step={step}
        precision={precision}
        stringMode
        onKeyPress={handleKeyPress}
        onPaste={handlePaste}
        style={{ width: '100%' }}
        addonAfter={suffix ? suffix : null}
        formatter={formatter || (isPricing ? formatCurrencyValue : null)}
        parser={parser || (isPricing ? parseCurrencyValue : null)}
        min={min}
      />
    </FormItemContainer>
  );
};

export const FormItemTextArea = ({
  property,
  placeholder,
  maxLength,
  style,
  rows,
  ...otherProps
}) => {
  return (
    <FormItemContainer property={property} style={style} {...otherProps}>
      <Input.TextArea
        placeholder={
          placeholder
            ? placeholder
            : `Please Input ${property.propertyDisplayName}`
        }
        rows={rows ?? 4}
        showCount={maxLength ? true : false}
        maxLength={maxLength}
      />
    </FormItemContainer>
  );
};

export const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 4 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
};

export const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 24, offset: 0 },
  },
};

export const renderInputFieldByVariant = (variant, displayName) => {
  switch (variant) {
    case 'number':
      return (
        <InputNumber
          placeholder={`Please Input ${displayName}`}
          autoFocus
          // style={{ width: '100%' }}
        />
      );
    case 'datetime':
      return <DatePicker format='MM-DD-YYYY' />;

    default:
      return (
        <Input
          placeholder={`Please Input ${displayName}`}
          autoFocus
          style={{ width: '100%' }}
        />
      );
  }
};

export const FormItemList = ({
  property,
  name,
  className,
  variant = 'text',
  productModules,
  ...otherProps
}) => {
  const dispatch = useProductDetailDispatch();

  const handleDeleteFieldForm = ({ field, removeCallback }) => {
    const isNestedField = Array.isArray(name);
    const indexesField = isNestedField
      ? name.concat(field.fieldKey)
      : [name].concat(field.fieldKey);

    const originalValueFieldForm = getFieldValueFromListIdx(
      productModules,
      indexesField
    );

    if (
      originalValueFieldForm === null ||
      originalValueFieldForm === undefined
    ) {
      removeCallback(field.name);
    } else {
      removeCallback(field.name);
      dispatch({
        type: productDetailActionTypes.UPDATE_DELETED_FIELDS_OVERLAY,
        pathField: generateOriginalStringPath(indexesField),
      });
    }
  };

  return (
    <Form.List name={name}>
      {(fields, { add, remove }, formListParams) => {
        const errors = formListParams?.errors;
        return (
          <Row className={className}>
            <FormItemContainer
              property={property}
              labelCol={{ span: 6 }}
              {...otherProps}
            >
              <Button
                type='default'
                onClick={() => add()}
                icon={<PlusOutlined />}
                style={{ borderRadius: 4 }}
              >
                Add value
              </Button>
            </FormItemContainer>

            {errors.length > 0 && (
              <Row style={{ width: '100%' }}>
                <Col offset={6} span={18}>
                  {errors[0]}
                </Col>
              </Row>
            )}

            <Col xs={6} />
            <Col flex={1}>
              <GroupElementWrapper
                style={{
                  display: fields.length === 0 ? 'none' : null,
                  marginRight: 40,
                  marginBottom: 12,
                  padding: '4px 12px 8px',
                  width: '96%',
                }}
              >
                {fields.map((field, index) => {
                  return (
                    <Row key={field.key}>
                      <Col flex='auto'>
                        <FormItemContainer
                          {...field}
                          {...(index === 0
                            ? formItemLayout
                            : formItemLayoutWithOutLabel)}
                        >
                          {renderInputFieldByVariant(
                            variant,
                            property.propertyDisplayName
                          )}
                        </FormItemContainer>
                      </Col>
                      {fields.length > 0 ? (
                        <Col flex='30px' style={{ textAlign: 'right' }}>
                          <Button
                            type='danger'
                            onClick={() => {
                              // remove(field.name);
                              handleDeleteFieldForm({
                                field,
                                removeCallback: remove,
                              });
                            }}
                            icon={<CloseCircleOutlined />}
                            style={{
                              marginLeft: 4,
                              marginTop: 6,
                              borderColor: '#ff4d4f',
                              backgroundColor: '#ff4d4f',
                              borderRadius: 4,
                            }}
                          />
                        </Col>
                      ) : null}
                    </Row>
                  );
                })}
              </GroupElementWrapper>
            </Col>
          </Row>
        );
      }}
    </Form.List>
  );
};

export const FormItemSelectEnum = ({
  property,
  nameFormItem,
  propertyEnums,
  disabled,
  onSelect = () => {},
  onClear = () => {},
  ...otherProps
}) => {
  const { propertyDisplayName, isArray } = property;

  return (
    <FormItemContainer property={property} name={nameFormItem} {...otherProps}>
      <WrapperSelect
        showSearch
        listHeight={140}
        mode={isArray ? 'multiple' : ''}
        placeholder={`Please Select ${propertyDisplayName}`}
        filterOption={(input, option) =>
          option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
        }
        disabled={disabled}
        onSelect={onSelect}
        onClear={onClear}
      >
        {propertyEnums?.map((property, index) => (
          <Select.Option
            value={property.enumDisplayName}
            key={property.enumDisplayName + index}
          >
            {property.enumDisplayName
              ? property.enumDisplayName
              : property.enumDescription}
          </Select.Option>
        ))}
      </WrapperSelect>
    </FormItemContainer>
  );
};

export const FormItemAllergenSelectEnum = ({
  property = {},
  nameFormItem,
  propertyEnums,
  parentNestedIndex,
  ...otherProps
}) => {
  const productDetailForm = Form.useFormInstance();

  const { propertyDisplayName, isArray, propertyName, fieldFullPath } =
    property;

  const indexLevelOfCode = parentNestedIndex.concat('LevelOfContainmentCode');
  const levelOfCodeValue = Form.useWatch(indexLevelOfCode, productDetailForm);

  const disabled = checkDisabledFormField({
    fieldFullPath,
    levelOfCode: levelOfCodeValue,
  });

  const requiredFields = checkRequiredFormField(levelOfCodeValue);

  return (
    <FormItemContainer
      property={property}
      name={nameFormItem}
      rules={[
        {
          required: requiredFields[propertyName],
          message: 'Please select your value!',
        },
      ]}
      {...otherProps}
    >
      <WrapperSelect
        showSearch
        listHeight={140}
        mode={isArray ? 'multiple' : ''}
        placeholder={`Please Select ${propertyDisplayName}`}
        filterOption={(input, option) =>
          option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
        }
        disabled={disabled}
        onChange={(value) => {
          if (
            propertyName === 'LevelOfContainmentCode' &&
            value?.toLowerCase() !== 'may contain'
          ) {
            const newFormValues = getFormValuesAfterUpdatingValueField({
              formValues: productDetailForm.getFieldsValue(),
              nestedIndex: parentNestedIndex.concat('ContainmentSource'),
              propertyName: 'ContainmentSource',
              value: null,
            });
            productDetailForm.setFieldsValue(newFormValues);
          }
        }}
      >
        {sortByKey(propertyEnums, 'enumDisplayName')?.map((property, index) => (
          <Select.Option
            value={property.enumDisplayName}
            key={property.enumDisplayName + index}
          >
            {property.enumDisplayName
              ? property.enumDisplayName
              : property.enumDescription}
          </Select.Option>
        ))}
      </WrapperSelect>
    </FormItemContainer>
  );
};

export const FormItemSelectSpecialEnum = ({
  property,
  nameFormItem,
  propertyEnums,
  ...otherProps
}) => {
  const { propertyDisplayName, isArray } = property;

  return (
    <FormItemContainer property={property} name={nameFormItem} {...otherProps}>
      <WrapperSelect
        listHeight={140}
        showSearch
        mode={isArray ? 'multiple' : ''}
        placeholder={`Please Select ${propertyDisplayName}`}
        filterOption={(input, option) =>
          option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
        }
      >
        {sortByKey(propertyEnums, 'enumCode')?.map((property, index) => (
          <Select.Option
            value={property.enumCode}
            key={property.enumCode + index}
          >
            {`${
              property.enumDescription
            } - (${property.enumCode.toUpperCase()})`}
          </Select.Option>
        ))}
      </WrapperSelect>
    </FormItemContainer>
  );
};

export const EmptyDataProduct = () => {
  return (
    <div className='product-detail-view__list-info product-detail-view__list-info--empty'>
      <Empty />
    </div>
  );
};

export const ErrorMessageProperty = ({ errors = [], ...rest }) => {
  return (
    <Alert
      className='product-detail-view__error-publication'
      message={errors[0]}
      type='error'
      // showIcon
      {...rest}
    />
  );
};

export const FormItemSelectCustomDropdown = ({
  property,
  propertyName,
  propertyDisplayName,
  propertyEnums,
  ...otherProps
}) => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const inputRef = useRef(null);
  const onNameChange = (event) => {
    setName(event.target.value);
  };
  const addItem = (e) => {
    e.preventDefault();
    setItems([...items, { value: name, label: name }]);
    setName('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  useEffect(() => {
    const newItems = sortByKey(propertyEnums, 'enumDisplayName')?.map(
      (property) => ({
        value: property.enumDisplayName,
        label: property.enumDisplayName
          ? property.enumDisplayName
          : property.enumDescription,
      })
    );
    setItems(newItems);
  }, [propertyEnums]);

  return (
    <FormItemContainer property={property} key={propertyName} {...otherProps}>
      <Select
        showSearch
        // mode={isArray ? 'multiple' : ''}
        placeholder={`Please Select ${propertyDisplayName}`}
        filterOption={(input, option) =>
          option?.label?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
        }
        dropdownRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: '8px 0' }} />
            <Space style={{ padding: '0 8px 4px' }}>
              <Input
                placeholder='Please enter item'
                ref={inputRef}
                value={name}
                onChange={onNameChange}
              />
              <Button
                type='text'
                disabled={Boolean(!name)}
                icon={<PlusOutlined />}
                onClick={addItem}
              >
                Add item
              </Button>
            </Space>
          </>
        )}
        options={items.map((item) => ({
          label: item?.label,
          value: item?.value,
        }))}
      />
    </FormItemContainer>
  );
};

export const FormItemIsFreeText = ({
  property,
  propertyName,
  propertyDisplayName,
  propertyEnums,
  ...otherProps
}) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const newItems = sortByKey(propertyEnums, 'enumDisplayName')?.map(
      (property) => ({
        value: property.enumDisplayName,
        label: property.enumDisplayName
          ? property.enumDisplayName
          : property.enumDescription,
      })
    );
    setItems(newItems);
  }, [propertyEnums]);

  return (
    <FormItemContainer property={property} key={propertyName} {...otherProps}>
      <AutoComplete
        allowClear
        options={items}
        placeholder={`Please Select ${propertyDisplayName}`}
        filterOption={(inputValue, option) => {
          return (
            option?.label?.toLowerCase()?.indexOf(inputValue?.toLowerCase()) >
            -1
          );
        }}
        getPopupContainer={(triggerNode) => {
          return triggerNode;
        }}
      />
    </FormItemContainer>
  );
};

export const FormItemCategory = ({
  property,
  propertyEnums,
  ...otherProps
}) => {
  const form = Form.useFormInstance();

  const handleResetValue = () => {
    // When category is selected, clear dependent fields
    form.resetFields(['SubCategory', 'Class']);
  };

  return (
    <FormItemSelectEnum
      property={property}
      nameFormItem='Category'
      propertyEnums={propertyEnums}
      onSelect={handleResetValue}
      onClear={handleResetValue}
      rules={[
        {
          required: true,
          message: 'Category is required',
        },
      ]}
      {...otherProps}
    />
  );
};

export const FormItemSubCategory = ({
  property,
  propertyEnums,
  ...otherProps
}) => {
  const form = Form.useFormInstance();
  const category = Form.useWatch('Category', form);

  const isDisabled = !category;
  let filteredEnums = propertyEnums;

  if (category) {
    const validSubcategories = getSubcategoriesByCategory(category);
    filteredEnums = validSubcategories
      .map((subcategory) =>
        propertyEnums.find((enumItem) => enumItem.enumCode === subcategory)
      )
      .filter(Boolean);
  }

  const handleResetValue = () => {
    form.resetFields(['Class']);
  };

  return (
    <FormItemSelectEnum
      property={property}
      nameFormItem='SubCategory'
      propertyEnums={filteredEnums}
      disabled={isDisabled}
      onSelect={handleResetValue}
      onClear={handleResetValue}
      rules={
        filteredEnums?.length > 0 && Boolean(category)
          ? [
              {
                required: true,
                message: 'Sub Category is required',
              },
            ]
          : []
      }
      {...otherProps}
    />
  );
};

export const FormItemClass = ({ property, propertyEnums, ...otherProps }) => {
  const form = Form.useFormInstance();
  const category = Form.useWatch('Category', form);
  const subCategory = Form.useWatch('SubCategory', form);

  if (category === CATEGORY_FIREARMS) {
    return null;
  }

  const isDisabled = !category || !subCategory;
  let filteredEnums = propertyEnums;

  if (category && subCategory) {
    const validClasses = getClassesByCategoryAndSubcategory(
      category,
      subCategory
    );
    filteredEnums = propertyEnums.filter((enumItem) =>
      validClasses.includes(enumItem.enumCode)
    );
  }

  return (
    <FormItemSelectEnum
      property={property}
      nameFormItem='Class'
      propertyEnums={filteredEnums}
      disabled={isDisabled}
      {...otherProps}
    />
  );
};
